import { OrganizationUIService } from './organizationUI.service';
import { OrganizationStoreService } from './store/organizationStore.service';
import { Injectable, Injector } from '@angular/core';
import { Globals } from '@smarttask-common/src/lib/services/globals';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { LocationService } from '../location.service';
import { SharedService } from '../AngularSpecific/shared.service';
import { OrganizationModel } from '@smarttask-common/src/lib/models/Organization/OrganizationModel';
import { DateService } from '@smarttask-common/src/lib/services/General/date.service';
import { DataModel } from '@smarttask-common/src/lib/models/DataModel';
import mixpanel from 'mixpanel-browser';
import { OrganizationPlanEnum } from '@smarttask-common/src/lib/models/Organization/OrganizationPlanEnum';
import { OrganizationSettingsModel } from '@smarttask-common/src/lib/models/Organization/settings/OrganizationSettingsModel';
import { StringService } from '@smarttask-common/src/lib/services/General/string.service';
import { ViewStoreService } from '../View/store/viewStore.service';
import { TimezoneModel } from '@smarttask-common/src/lib/models/General/TimezoneModel';
import { DefaultSetupService } from '../Internal/RegistrationGoal/defaultSetup.service';
declare let Intercom;
declare let cioanalytics;

@Injectable({
    providedIn: 'root',
})
export class OrganizationService {
    constructor(
        private injector: Injector,
        private globals: Globals,
        private http: HttpClient,
        private locationService: LocationService,
        private sharedService: SharedService,
        private stringServie: StringService,
        private dateService: DateService,
        private organizationStoreService: OrganizationStoreService,
        private organizationUIService: OrganizationUIService,
        private viewStoreService: ViewStoreService,
        private defaultSetupService: DefaultSetupService,
    ) {
        this.organizationStoreService.watchSelectedOrganizationId().subscribe((organization_id) => {
            const organization = this.organizationStoreService.getByOrganizationId(organization_id);
            const subscription = organization?.subscription;
            if (subscription?.max_file_size_mb) {
                this.globals.max_file_size_mb = subscription.max_file_size_mb;
            }
            if (subscription?.max_video_file_size_mb) {
                this.globals.max_video_file_size_mb = subscription.max_video_file_size_mb;
            }
        });
    }

    create = (org: OrganizationModel, is_default: boolean = false): Promise<any> => {
        return this.http
            .post(`${this.globals.api_base_url}${this.globals.api_version}/organization/create/${is_default}`, org)
            .toPromise()
            .then((organization: OrganizationModel) => {
                organization = this.organizationUIService.organizationReceivedProcessing(organization);
                this.organizationStoreService.upsertOne(organization);
                return organization;
            });
    };

    fetchOrganizations = () => {
        return this.http
            .get(`${this.globals.api_base_url}${this.globals.api_version}/organization/list`)
            .toPromise()
            .then((organizations: Array<OrganizationModel>) => {
                organizations = organizations.map((x) => this.organizationUIService.organizationReceivedProcessing(x));

                //Update store
                this.organizationStoreService.upsertMany(organizations);

                organizations.forEach((x) => {
                    if (typeof Intercom != 'undefined' && Intercom != undefined) {
                        Intercom('update', {
                            company: {
                                id: x.organization_id,
                                name: x.name,
                                industry: x.industry,
                                plan: x.subscription.plan,
                                expires: x.subscription.expires_at,
                            },
                        });
                    }
                    //Send data to Mixpanel
                    if (mixpanel != undefined && this.globals.mixpanel_loaded) {
                        let organization_id = x.organization_id.toString();
                        mixpanel.add_group('organization_id', organization_id);
                        mixpanel.get_group('organization_id', organization_id).set({
                            organization_id: organization_id,
                            $name: x.name,
                            industry: x.industry,
                            plan: x.subscription.plan,
                            expires: x.subscription.expires_at,
                        });
                    }

                    if(typeof cioanalytics != 'undefined' && cioanalytics != undefined){
                        cioanalytics.group(x.organization_id.toString(),{
                            name: x.name,
                            industry: x.industry,
                            plan: x.subscription.plan,
                            expires: x.subscription.expires_at
                        })
                    }
                });

                return organizations;
            });
    };

    fetchDefaultOrganization = () => {
        return this.http
            .get(`${this.globals.api_base_url}${this.globals.api_version}/organization/default`)
            .toPromise()
            .then((organization: OrganizationModel) => {
                if (organization == undefined) {
                    //No default organization
                    return undefined;
                }
                organization = this.organizationUIService.organizationReceivedProcessing(organization);
                this.organizationStoreService.upsertOne(organization);
                return organization;
            });
    };

    fetchOrganization = (organization_id) => {
        return this.http
            .get(`${this.globals.api_base_url}${this.globals.api_version}/organization/${organization_id}`)
            .toPromise()
            .then((organization: OrganizationModel) => {
                organization = this.organizationUIService.organizationReceivedProcessing(organization);
                this.organizationStoreService.upsertOne(organization);
                return organization;
            });
    };

    fetchOrganizationByUUID = (organization_uuid: string) => {
        return this.http
            .get(`${this.globals.api_base_url}${this.globals.api_version}/organization/by-uuid/${organization_uuid}`)
            .toPromise()
            .then((organization: OrganizationModel) => {
                organization = this.organizationUIService.organizationReceivedProcessing(organization);
                this.organizationStoreService.upsertOne(organization);
                return organization;
            });
    };

    fetchOrganizationUuid = (organization_id: number) => {
        return this.http
            .get(`${this.globals.api_base_url}${this.globals.api_version}/organization/uuid-value/${organization_id}`)
            .toPromise()
            .then((data: any) => {
                return data.uuid;
            });
    };

    fetchOrganizationWhiteLabel = (organization_uuid: string) => {
        return this.http
            .get(`${this.globals.api_base_url}${this.globals.api_version}/organization/org-and-whitelabel/${organization_uuid}`)
            .toPromise()
            .then((data: any) => {
                const organization = this.organizationUIService.organizationReceivedProcessing(data.organization);
                this.organizationStoreService.upsertOne(organization);

                const white_label = data.white_label;
                if (white_label) {
                    this.globals.whitelabel = white_label;
                }
                return { organization, white_label };
            });
    };

    setAsDefault = (organization_id: number) => {
        return this.http
            .get(`${this.globals.api_base_url}${this.globals.api_version}/organization/set-as-default/${organization_id}`)
            .toPromise()
            .then(() => {
                let organizations = structuredClone(this.organizationStoreService.getlist());
                organizations.forEach((org) => {
                    org.is_default = false;
                    if (org.organization_id == organization_id) {
                        org.is_default = true;
                    }
                });
                this.organizationStoreService.upsertMany(organizations);
                return;
            });
    };

    editName = (name: string): Promise<any> => {
        let organization_id = this.organizationStoreService.getSelectedOrganizationId();
        //Update store
        let changes: Partial<OrganizationModel> = {
            name: name,
        };
        this.organizationStoreService.updateOne(organization_id, changes);

        return this.http
            .post(`${this.globals.api_base_url}${this.globals.api_version}/organization/edit-name/${organization_id}`, { name: name })
            .toPromise()
            .then((response) => {
                return;
            });
    };

    updateSettings = (model: OrganizationSettingsModel): Promise<any> => {
        let organization_id = this.organizationStoreService.getSelectedOrganizationId();
        //Update store
        let changes: Partial<OrganizationModel> = {
            settings: model,
        };
        this.organizationStoreService.updateOne(organization_id, changes);

        return this.http
            .post(`${this.globals.api_base_url}${this.globals.api_version}/organization/update-settings/${organization_id}`, model)
            .toPromise()
            .then((response) => {
                return;
            });
    };

    updateTimeTrackingByDefault = (show_time_tracking_by_default: boolean) => {
        let organization = this.organizationStoreService.getSelectedOrganization();
        let settings = structuredClone(organization.settings);
        settings.show_time_tracking_by_default = show_time_tracking_by_default;
        //Update store
        let changes: Partial<OrganizationModel> = {
            settings: settings,
        };
        return this.http
            .get(
                `${this.globals.api_base_url}${this.globals.api_version}/organization/show-time-tracking-by-default/${organization.organization_id}/${show_time_tracking_by_default}`,
            )
            .toPromise();
    };

    updateIcon = (model: DataModel) => {
        let organization_id = this.organizationStoreService.getSelectedOrganizationId();
        return this.http
            .post(`${this.globals.api_base_url}${this.globals.api_version}/organization/update-icon/${organization_id}`, model, {
                responseType: 'text',
            })
            .toPromise()
            .then((url: string) => {
                //Refresh all references of the current user
                let changes: Partial<OrganizationModel> = {
                    icon_url: url,
                };
                this.organizationStoreService.updateOne(organization_id, changes);
                return url;
            });
    };

    updateTimezone = (model: TimezoneModel): Promise<any> => {
        let organization_id = this.organizationStoreService.getSelectedOrganizationId();
        //Update store
        let changes: Partial<OrganizationModel> = {
            timezone: model,
        };
        this.organizationStoreService.updateOne(organization_id, changes);

        return this.http
            .post(`${this.globals.api_base_url}${this.globals.api_version}/organization/update-timezone/${organization_id}`, model)
            .toPromise()
            .then((response) => {
                return;
            });
    };
    deleteOrganization = (organization_id: number) => {
        return this.http
            .delete(`${this.globals.api_base_url}${this.globals.api_version}/organization/delete/${organization_id}`)
            .toPromise()
            .then((response) => {
                this.organizationStoreService.deleteOne(organization_id);
                return;
            });
    };

    changeSelectedOrganization = async (organization_id) => {
        let selected_organization_id = 0;
        let organization = this.organizationStoreService.getByOrganizationId(organization_id);
        if (!organization) {
            //Try and load the organization from server
            try {
                await this.fetchOrganization(organization_id);
            } catch (er) {
                console.log('Fetching Organization Failed');
                console.log(er);
            }
            organization = this.organizationStoreService.getByOrganizationId(organization_id);
        }
        if (organization) {
            selected_organization_id = organization_id;
        } else {
            //Incase the organization_id entered in url doesn't match any organizations loaded. Change the route
            this.goToDefaultOrganization('change select organization');
            return Promise.reject('default organization');
        }

        this.viewStoreService.deleteAll(); //This is added as otherwise custom-field list store change triggers copying view-list again which is not ideal and causes edge cases
        this.organizationStoreService.updateSelectedOrganizationId(selected_organization_id);
        return Promise.resolve();
    };

    //Go to default organization view from all the organizations loaded
    goToDefaultOrganization = (from: string): boolean => {
        console.log('go to default organization triggered from -' + from);
        let organizations = this.organizationStoreService.getlist();
        if (organizations.length <= 0) {
            return true;
        }
        let default_organization = organizations.find((x) => x.is_default == true);
        //If there is no default organization, select first organization
        if (default_organization == undefined) {
            default_organization = organizations[0];
        }
        this.locationService.default_location(default_organization.organization_id);
        this.defaultSetupService.checkDefaultProjectProcessingCount();
        return false;
    };

    getOrganizationPlanInString = (type: OrganizationPlanEnum, version_suffix = true): string => {
        let to_ret: string = this.stringServie.toTitleCase(type);
        if (version_suffix) to_ret += ' Version';
        return to_ret;
    };

    isGraceExpired = (): boolean => {
        let selected_organization = this.organizationStoreService.getSelectedOrganization();
        if (
            selected_organization.subscription.plan != OrganizationPlanEnum.free && 
            selected_organization.subscription.grace_expires_at < new Date()
        ) {
            return true;
        }
        return false;
    };

    isExpired = (): boolean => {
        let selected_organization = this.organizationStoreService.getSelectedOrganization();
        if (
            selected_organization.subscription.plan != OrganizationPlanEnum.free && 
            selected_organization.subscription.expires_at < new Date()
        ) {
            return true;
        }
        return false;
    };
}
